import * as graph from '@adc/sdk'
import analytics from '@adc/analytics'
import instrument from '@adc/instrument'
import {
  mapPinTrackingType,
  getMapPinTrackingInfo,
} from '../components/marker/helpers'
import { getExperimentGroup, getSortApplied } from '../constants/sorting'

const { eventNames, track } = analytics

const SUCCESS_STATUS = 'success'

/**
 * Builds the shared sort-experiment context fields used across
 * select_item, srpPropertyClick, and srpPropertySave events.
 */
const getSortExperimentContext = ({
  experimentFlagValue,
  sortSelection,
  index,
  listing,
  resultSection,
  numSearchResultsWithin,
  numSearchResultsNearby,
  searchResultsRadius,
}) => {
  const experimentGroup = getExperimentGroup(experimentFlagValue)
  const sortApplied = getSortApplied(experimentFlagValue, sortSelection?.type)
  const auctionDateRaw =
    listing?.selling_method?.__typename === 'OnlineAuctionSegment'
      ? listing.selling_method.start_date
      : listing?.auction?.visible_auction_start_date_time
  const daysToAuction = auctionDateRaw
    ? Math.ceil((new Date(auctionDateRaw) - Date.now()) / (1000 * 60 * 60 * 24))
    : null

  return {
    experiment_group: experimentGroup,
    sort_applied: sortApplied,
    position: index ?? null,
    days_to_auction: daysToAuction,
    result_section: resultSection || null,
    numSearchResultsWithin: numSearchResultsWithin ?? null,
    numSearchResultsNearby: numSearchResultsNearby ?? null,
    search_results_radius: searchResultsRadius ?? null,
  }
}
const ASSET_INTERACTION_METRIC_NAME = 'srp_asset_action'
export const ONE_CLICK_SAVE_SEARCH_START = eventNames.oneClickSaveSearchStart
export const ONE_CLICK_SAVE_SEARCH_SUCCESS =
  eventNames.oneClickSaveSearchSuccess
export const ONE_CLICK_SAVE_SEARCH_UPDATE_NAME =
  eventNames.oneClickSaveSearchUpdateName
export const ONE_CLICK_SAVE_SEARCH_REMOVE = eventNames.oneClickSaveSearchRemove
export const ONE_CLICK_SAVE_SEARCH_CLOSE = eventNames.oneClickSaveSearchClose
export const MOBILE_LISTING_CARD = 'mobile_listing_card'
export const LISTING_CARD = 'listing_card'
export const SRP_ITEM_LIST_ID = 'srp'
export const SRP_ITEM_LIST_NAME = 'search_results_page'

// Amplitude tracking for search filter changes. blueprint emits the diff
// (FilterChange) via useListingsSearch's onFilterChanged; the mapper lives in
// @adc/analytics and is scoped to the FILTERS bucket.
export const trackSearchFilterChanged = analytics.trackSearchFilterChanged

const defaultEmptyValue = 'none'
const defaultBadgesList = [defaultEmptyValue]

const mapTriggerToVersion = {
  asset_row: 'v1',
  asset_tile: 'v1',
  listing_card: 'v2',
  mobile_listing_card: 'v2',
  search_results_list: 'v1',
}

const getAnalyticsBadgeInfo = badges => {
  return {
    propertyBadges: badges.map(badge => badge.replace(/\s/g, '')).join(),
  }
}

const instrumentAssetInteraction = ({
  interactionName,
  triggeredFrom,
  isReserveMetShown,
  displayedCurrentBid,
}) => {
  instrument.increment(ASSET_INTERACTION_METRIC_NAME, 1, [
    `asset_interaction_name:${interactionName}`,
    `version:${mapTriggerToVersion[triggeredFrom]}`,
    `trigger:${triggeredFrom}`,
    `is_reserve_met_shown:${Boolean(isReserveMetShown)}`,
    `is_current_bid_shown:${Boolean(displayedCurrentBid)}`,
  ])
}

const trackPropertyClick = ({
  listing,
  triggeredFrom,
  badges = defaultBadgesList,
  isReserveMetShown = false,
  displayedCurrentBid,
  displayedERV,
  itemListId,
  itemListName,
  index,
  numberSearchResults,
  sortSelection,
  experimentFlagValue,
  resultSection,
  numSearchResultsWithin,
  numSearchResultsNearby,
  searchResultsRadius,
}) => {
  instrument.domainEvent({
    type: graph.enums.DomainEventTypeName.ListingCardClicked,
    source: listing.urn,
    data: {
      triggered_from: triggeredFrom,
      is_reserve_met_shown: isReserveMetShown,
      is_current_bid_shown: displayedCurrentBid,
      is_erv_shown: displayedERV,
    },
  })
  instrumentAssetInteraction({
    interactionName: 'select',
    triggeredFrom,
    isReserveMetShown,
    displayedCurrentBid,
  })

  const sortExperimentContext = getSortExperimentContext({
    experimentFlagValue,
    sortSelection,
    index,
    listing,
    resultSection,
    numSearchResultsWithin,
    numSearchResultsNearby,
    searchResultsRadius,
  })

  if (listing && itemListId && itemListName) {
    const trackingData = analytics.standardTracking.listing(listing)

    const item = {
      item_id:
        trackingData.morpheusPropertyId ||
        listing?.primary_property?.property_id,
      item_name: listing?.formatted_address?.[0] || 'Unknown Property',
      morpheus_property_id:
        trackingData.morpheusPropertyId ||
        listing?.primary_property?.property_id,
      item_list_id: itemListId,
      item_list_name: itemListName,
      currency: 'USD',
      index,
      item_category: listing?.listing_configuration?.asset_type,
      item_category2: listing?.primary_property?.summary?.structure_type_code,
      price: listing?.primary_property?.summary?.valuation,
      quantity: 1,
      recommendationType: itemListId,
    }

    const selectItemPayload = {
      ecommerce: {
        item_list_id: itemListId,
        item_list_name: itemListName,
        items: [item],
      },
      ...sortExperimentContext,
    }

    track('ecommerce_clear', { ecommerce: null })
    track(eventNames.selectItem, selectItemPayload)
  }

  const eventName =
    triggeredFrom === MOBILE_LISTING_CARD
      ? eventNames.srpPropertyClickMobile
      : eventNames.srpPropertyClick

  return track(eventName, {
    status: SUCCESS_STATUS,
    ...analytics.standardTracking.listing(listing),
    ...getAnalyticsBadgeInfo(badges),
    currentBid: displayedCurrentBid || defaultEmptyValue,
    erv: displayedERV || defaultEmptyValue,
    recommendationType: itemListId,
    numberSearchResults,
    sortSelection,
    ...sortExperimentContext,
  })
}

const trackViewItemList = ({
  listings,
  itemListId,
  itemListName,
  numberSearchResults,
  sortSelection,
  experimentFlagValue,
  numSearchResultsWithin,
  numSearchResultsNearby,
  searchResultsRadius,
}) => {
  if (!itemListId || !itemListName || !listings?.length) return

  const items = listings.map((item, idx) => {
    const listing = item.listing || item
    const listingIndex = item.index !== undefined ? item.index : idx

    const trackingData = analytics.standardTracking.listing(listing)
    return {
      item_id:
        trackingData.morpheusPropertyId ||
        listing?.primary_property?.property_id,
      item_name: listing?.formatted_address?.[0] || 'Unknown Property',
      morpheus_property_id:
        trackingData.morpheusPropertyId ||
        listing?.primary_property?.property_id,
      item_list_id: itemListId,
      item_list_name: itemListName,
      currency: 'USD',
      index: listingIndex,
      item_category: listing?.listing_configuration?.asset_type,
      item_category2: listing?.primary_property?.summary?.structure_type_code,
      price: listing?.primary_property?.summary?.valuation,
      quantity: 1,
    }
  })

  const experimentGroup = getExperimentGroup(experimentFlagValue)
  const sortApplied = getSortApplied(experimentFlagValue, sortSelection?.type)

  const trackingPayload = {
    ecommerce: {
      item_list_id: itemListId,
      item_list_name: itemListName,
      numberSearchResults,
      sortSelection,
      items,
    },
    experiment_group: experimentGroup,
    sort_applied: sortApplied,
    numSearchResultsWithin: numSearchResultsWithin ?? null,
    numSearchResultsNearby: numSearchResultsNearby ?? null,
    search_results_radius: searchResultsRadius ?? null,
  }

  track('ecommerce_clear', { ecommerce: null })
  track(eventNames.viewItemList, trackingPayload)
}

const trackPropertySave = ({
  listing,
  triggeredFrom,
  isSaved,
  badges = defaultBadgesList,
  isReserveMetShown,
  displayedCurrentBid,
  displayedERV,
  recommendationType,
  numberSearchResults,
  sortSelection,
  experimentFlagValue,
  resultSection,
  position,
  numSearchResultsWithin,
  numSearchResultsNearby,
  searchResultsRadius,
}) => {
  instrumentAssetInteraction({
    interactionName: isSaved ? 'unsave' : 'save',
    triggeredFrom,
    isReserveMetShown,
    isCurrentBidShown: Boolean(displayedCurrentBid),
  })

  const sortExperimentContext = getSortExperimentContext({
    experimentFlagValue,
    sortSelection,
    index: position,
    listing,
    resultSection,
    numSearchResultsWithin,
    numSearchResultsNearby,
    searchResultsRadius,
  })

  return track(eventNames.srpPropertySave, {
    status: SUCCESS_STATUS,
    action: isSaved ? 'remove' : 'save',
    ...analytics.standardTracking.listing(listing),
    ...getAnalyticsBadgeInfo(badges),
    propertyAddress: listing?.formatted_address?.[0],
    currentBid: displayedCurrentBid || defaultEmptyValue,
    erv: displayedERV || defaultEmptyValue,
    recommendationType,
    numberSearchResults,
    sortSelection,
    ...sortExperimentContext,
  })
}

export const trackSaveSearch = ({
  event,
  user,
  ...additionalSavedSearchTracking
}) => {
  return track(event, {
    status: SUCCESS_STATUS,
    ...analytics.standardTracking.user(user),
    ...additionalSavedSearchTracking,
  })
}

const getMapPinAdditionalTrackingObject = (listing, featureFlags = {}) => {
  let mapPinType = mapPinTrackingType.ORIGINAL
  const currentMapPinActiveTrackingVariant = getMapPinTrackingInfo(
    listing,
    featureFlags
  )
  if (currentMapPinActiveTrackingVariant[mapPinTrackingType.UPDATED]) {
    mapPinType = mapPinTrackingType.UPDATED
  } else if (currentMapPinActiveTrackingVariant[mapPinTrackingType.NEW]) {
    mapPinType = mapPinTrackingType.NEW
  } else if (currentMapPinActiveTrackingVariant[mapPinTrackingType.SEEN]) {
    mapPinType = mapPinTrackingType.SEEN
  }
  return {
    mapPinType,
    mapPinSeen: getMapPinTrackingInfo(listing, featureFlags)[
      mapPinTrackingType.SEEN
    ],
  }
}

const trackPageLoadWithFlags = (user = {}, featureFlags = {}) => {
  // This condition is used to determine which SEARCH button combination was
  // displayed on page load

  const additionalData = {
    testId1: '10010',
    testVariant1: featureFlags['fe.resi.enable-new-navigation']
      ? 'new_nav_feb26'
      : 'control_nav',
    testId3: '30002',
    testVariant3: 'ss2',
    ...(featureFlags['fe.resi.ab.testing.onboarding_drawer'] &&
      featureFlags['fe.resi.ab.testing.onboarding_drawer']?.test),
    ...(featureFlags['fe.resi.ab.testing.signup-propositions'] &&
      featureFlags['fe.resi.ab.testing.signup-propositions']?.test),
    ...(featureFlags['fe.resi.ab.testing.onboarding_drawer_content'] &&
      featureFlags['fe.resi.ab.testing.onboarding_drawer_content']?.test),
    ...(featureFlags['fe.resi.ab.testing.srp.mobile.popular.filters'] &&
      featureFlags['fe.resi.ab.testing.srp.mobile.popular.filters']?.test),
    ...(featureFlags['fe.resi.enabled-photo-enhancement'] &&
      featureFlags['fe.resi.enabled-photo-enhancement']?.test),
    ...(featureFlags['fe.resi.ab.testing.signup.thank-you-page'] &&
      featureFlags['fe.resi.ab.testing.signup.thank-you-page']?.test),
    ...(featureFlags['fe.resi.srp-sort-experiment-v1'] && {
      testId7: '70002',
      testVariant7:
        featureFlags['fe.resi.srp-sort-experiment-v1'] === 'treatment'
          ? 'srp_sort_group_b'
          : 'srp_sort_group_a',
    }),
  }

  return trackPageLoad(user, additionalData)
}

const trackPageLoad = (user = {}, additionalData) => {
  analytics.init(window.CONFIGS).then(() => {
    analytics.trackDataLayerLoaded(user, {
      ...additionalData,
      status: SUCCESS_STATUS,
    })
  })
}

// to be fired when using ajax search
// virtualTitle: <location><tps/reo/all><online/in person>
const trackVirtualSearchResults = (
  newSearchReqUrl,
  numberSearchResults,
  virtualTitle
) => {
  const virtualSearchResultsPage = {
    status: SUCCESS_STATUS,
    newSearch: newSearchReqUrl,
    numberSearchResults,
    virtualTitle,
    virtualUrl: window.location.pathname,
  }

  track(eventNames.virtualPageview, virtualSearchResultsPage)
}

// virtualTitle: <location><tps/reo/all><online/in person>
const getVirtualTitle = filterData => {
  let assetType = 'all'
  let assetLocation = 'all'
  const locationParts = []
  // location search
  if (filterData.property_city) locationParts.push(filterData.property_city)
  if (filterData.property_county) locationParts.push(filterData.property_county)
  if (filterData.property_state) locationParts.push(filterData.property_state)
  if (filterData.property_zip) locationParts.push(filterData.property_zip)
  const location = locationParts.join(' ')
  // asset type
  const isReoSearch =
    filterData.usecode_product_type &&
    filterData.usecode_product_type.indexOf('resi_new_foreclosed') >= 0
  const isTpsSearch =
    filterData.usecode_product_type &&
    filterData.usecode_product_type.indexOf('resi_ft') >= 0
  if (isReoSearch && !isTpsSearch) assetType = 'reo'
  if (isTpsSearch && !isReoSearch) assetType = 'tps'
  // asset location
  if (
    Object.prototype.hasOwnProperty.call(filterData, 'is_asset_online_event')
  ) {
    assetLocation =
      filterData.is_asset_online_event === true ? 'online' : 'in person' // eslint-disable-line
  }
  return `${location}/${assetType}/${assetLocation}`
}

const trackMapPin = ({
  listing = {},
  isMobileView,
  isCurrentBidShown,
  isReserveMetShown = false,
  featureFlags = {},
  isHoverEvent = false,
}) => {
  instrument.increment(ASSET_INTERACTION_METRIC_NAME, 1, [
    `asset_interaction_name:${
      isMobileView ? 'mobile-map-card-open' : 'select'
    }`,
    `trigger:map_marker`,
    `is_mobile:${Boolean(isMobileView)}`,
    `version:v2`,
    `is_current_bid_shown:${Boolean(isCurrentBidShown)}`,
    `is_reserve_met_shown:${Boolean(isReserveMetShown)}`,
  ])
  let eventName = eventNames.mapClick
  if (isMobileView) {
    eventName = eventNames.mapClickMobile
  } else if (isHoverEvent) {
    // only send hover event if we're not mobile and isHoverEvent is true
    eventName = eventNames.mapPinHover
  }
  return track(eventName, {
    status: SUCCESS_STATUS,
    ...analytics.standardTracking.listing(listing),
    ...getMapPinAdditionalTrackingObject(listing, featureFlags),
  })
}

export {
  getVirtualTitle,
  trackVirtualSearchResults,
  trackPageLoadWithFlags,
  trackMapPin,
  trackPropertyClick,
  trackPropertySave,
  trackViewItemList,
}
